<script lang="ts">
	import { devices_settings } from 'src/stores';
	import { onMount } from 'svelte';
	import { Button, Input, Radio, Text } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		WAVE_CONFIGURATION_PARAM_META,
		type WaveConfigurationParamMeta,
	} from 'src/helpers/z_wave_settings_config';
	import {
		fetchWaveConfigurationParam,
		mergeWaveConfigurationParam,
		parseConfigurationGetValue,
	} from 'src/helpers/z_wave_configuration_helpers';
	import { zwaveCall } from 'src/services/xz_http_reqs';
	import { format } from 'svelte-i18n';

	export let device;
	export let param: number;

	const id = real_id(device.id);
	const paramKey = String(param);
	const meta: WaveConfigurationParamMeta = WAVE_CONFIGURATION_PARAM_META[paramKey] || {
		title: `wave_param_${paramKey}`,
		titleDefault: `Parameter ${paramKey}`,
		description: `wave_param_${paramKey}_desc`,
		descriptionDefault: `Device configuration parameter ${paramKey}.`,
		control: 'number',
		min: 0,
		max: 2147483647,
	};

	let editValue: number | string = '';
	let cacheValue: number | string = '';
	let loading = false;
	let fetching = false;
	let unavailable = false;
	let lastSynced: number | string | undefined;

	$: settings = $devices_settings?.[real_id(id)];
	$: storedValue = settings?.['Configuration:0']?.[paramKey];
	$: if (typeof storedValue === 'number') {
		unavailable = false;
		syncFromStore(storedValue);
	}

	const syncFromStore = (value: number) => {
		if (loading) {
			return;
		}
		if (lastSynced === value) {
			return;
		}
		lastSynced = value;
		editValue = value;
		cacheValue = value;
	};

	$: rangeError =
		meta.control === 'number' &&
		editValue !== '' &&
		(typeof meta.min === 'number' || typeof meta.max === 'number') &&
		(+editValue < (meta.min ?? -Infinity) || +editValue > (meta.max ?? Infinity))
			? $format('generic_out_of_range', { default: 'Value out of range' })
			: '';

	const clampNumber = (value: number): number => {
		let next = value;
		if (typeof meta.max === 'number' && next > meta.max) {
			next = meta.max;
		}
		if (typeof meta.min === 'number' && next < meta.min) {
			next = meta.min;
		}
		return next;
	};

	const setSelectValue = (value: number) => {
		editValue = value;
	};

	const saveValue = async () => {
		if (meta.control === 'readonly' || unavailable) {
			return;
		}
		if (editValue === '' || Number.isNaN(+editValue)) {
			return;
		}

		const nextValue = meta.control === 'number' ? clampNumber(+editValue) : +editValue;
		editValue = nextValue;
		if (cacheValue === nextValue) {
			return;
		}

		loading = true;
		const res = await zwaveCall(device, {
			cc: 'Configuration',
			m: 'set',
			ep: 0,
			params: [{ parameter: param, value: nextValue }],
		});

		if (res.isok) {
			const resGet = await zwaveCall(device, {
				cc: 'Configuration',
				m: 'get',
				ep: 0,
				params: [param],
			});
			const fetched = parseConfigurationGetValue(resGet);
			const confirmed = typeof fetched === 'number' ? fetched : nextValue;
			mergeWaveConfigurationParam(id, param, confirmed);
			editValue = confirmed;
			cacheValue = confirmed;
			lastSynced = confirmed;
		}

		await onSettingsResponse(res, device);
		loading = false;
	};

	onMount(async () => {
		if (typeof storedValue === 'number') {
			return;
		}
		fetching = true;
		const result = await fetchWaveConfigurationParam(device, paramKey);
		fetching = false;
		if (!result.ok) {
			unavailable = true;
		}
	});
</script>

<Text styleType="body-small" class="mb-s">
	{$format(meta.description, { default: meta.descriptionDefault })}
</Text>

{#if fetching}
	<Text styleType="body-small">
		{$format('loading', { default: 'Loading…' })}
	</Text>
{:else if unavailable}
	<Text styleType="body-small">
		{$format('wave_param_unavailable', {
			default: 'This setting is unavailable on this device.',
		})}
	</Text>
{:else if meta.control === 'readonly'}
	<Text styleType="controls-large">
		{typeof storedValue === 'undefined' ? '—' : storedValue}
	</Text>
{:else if meta.control === 'select' && meta.options}
	{#each meta.options as option}
		<Radio
			size="m"
			selected={meta.options.find((entry) => entry.value === +editValue)?.label}
			value={option.label}
			on:click={() => setSelectValue(option.value)}
			id={`wave-param-${param}-${option.value}`}
		>
			{$format(option.label, { default: option.labelDefault })}
		</Radio>
	{/each}
	<Button class="mt-s" {loading} size="m" width="full" on:click={saveValue}>
		{$format('button_save')}
	</Button>
{:else}
	<Input
		class="mb-s"
		size="m"
		bind:value={editValue}
		type="number"
		min={meta.min}
		max={meta.max}
		error={rangeError}
		step={1}
		id={`wave-param-input-${param}`}
		aria-label={$format(meta.title, { default: meta.titleDefault })}
		on:enter={saveValue}
	/>
	<Button class="mt-s" {loading} size="m" width="full" on:click={saveValue}>
		{$format('button_save')}
	</Button>
{/if}
